import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Database, Plus, RefreshCw, Settings } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { ConfigurationWizard } from '@/components/netapp/ConfigurationWizard';
import { netappAPI, type NetAppConfig } from '@/lib/api/netapp';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

function since(date: string | null | undefined) {
  return date ? formatDistanceToNow(new Date(date.endsWith('Z') ? date : `${date}Z`), { addSuffix: true }) : 'Not yet collected';
}

function ConnectionCard({ config, onEdit }: { config: NetAppConfig; onEdit: () => void }) {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const { data: customers = [], isLoading, isError } = useQuery({
    queryKey: ['aiq-customers', config.id],
    queryFn: () => netappAPI.getConnectionCustomers(config.id),
    refetchInterval: 5000,
  });
  const ready = customers.filter(c => c.last_collected_at).length;
  const collecting = customers.filter(c => c.collection_status === 'running').length;
  const failed = customers.filter(c => c.collection_status === 'failed').length;
  const filtered = customers.filter(c => `${c.customer_name} ${c.customer_id}`.toLowerCase().includes(search.toLowerCase()));
  const refresh = async () => {
    setRefreshing(true);
    try {
      await netappAPI.refreshConnection(config.id);
      await queryClient.invalidateQueries({ queryKey: ['aiq-customers', config.id] });
      toast.success('Customer discovery and data refresh queued');
    } catch {
      toast.error('Unable to queue a refresh. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };
  return (
    <section className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{config.name || 'NetApp AIQ'}</h2>
          <p className="text-sm text-gray-500 mt-1">{config.is_active ? 'Automatic collection enabled' : 'Connection paused'} · All accessible customers</p>
          <p className="text-sm text-gray-500">Customer directory: {config.last_discovered_at ? since(config.last_discovered_at) : 'Awaiting connection'}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onEdit} className="border rounded-lg px-3 py-2 text-sm">Update connection</button>
          <button onClick={refresh} disabled={refreshing || !config.is_active} className="flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />Refresh all customers</button>
        </div>
      </div>
      {config.discovery_error && <p role="alert" className="rounded-lg bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-200">{config.discovery_error}</p>}
      <div className="flex flex-wrap gap-6 text-sm">
        <span><strong>{customers.length}</strong> customers</span><span><strong>{ready}</strong> collected</span><span><strong>{collecting}</strong> collecting</span><span><strong>{failed}</strong> need attention</span>
      </div>
      <p className="text-sm text-gray-500">Operational data refreshes in the background every 15 minutes, with full inventory collected daily. Customer views use stored data while refreshing. Large estates may take longer to collect.</p>
      <label className="block text-sm font-medium">Find a collected customer
        <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or customer ID" className="block mt-1 w-full sm:max-w-sm border rounded-lg px-3 py-2 dark:bg-slate-800 dark:border-slate-600" />
      </label>
      {isLoading ? <p role="status">Loading customer status…</p> : isError ? <p role="alert">Unable to load customer status.</p> : customers.length === 0 ? <p className="text-sm text-gray-500">Customers will appear after AIQ discovery completes.</p> : (
        <div className="overflow-auto max-h-[32rem]">
          <table className="w-full text-sm text-left">
            <thead className="text-gray-500"><tr><th className="py-2">Customer</th><th>Systems</th><th>Collection</th><th>Last successful refresh</th><th><span className="sr-only">View</span></th></tr></thead>
            <tbody>{filtered.map(customer => <tr key={customer.customer_id} className="border-t dark:border-slate-700">
              <td className="py-3 pr-3">{customer.customer_name}<span className="block text-xs text-gray-500">{customer.customer_id}</span></td>
              <td className="pr-3">{customer.system_count}</td>
              <td className="pr-3"><span>{customer.collection_status === 'pending' ? 'Queued' : customer.collection_status === 'ready' ? 'Ready' : customer.collection_status === 'running' ? 'Collecting' : 'Retry needed'}</span>{customer.collection_error && <span className="block max-w-xs text-xs text-amber-600">{customer.collection_error}</span>}</td>
              <td className="pr-3">{since(customer.last_collected_at)}</td>
              <td><Link className="text-indigo-600 dark:text-indigo-300" to={`/dashboard?customer=${encodeURIComponent(customer.customer_id)}`}>View dashboard</Link></td>
            </tr>)}</tbody>
          </table>
          {filtered.length === 0 && <p className="py-3 text-sm">No matching customers</p>}
        </div>
      )}
    </section>
  );
}

export function NetAppConfiguration() {
  const { configId } = useParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<NetAppConfig | null | undefined>(undefined);
  const { data: configs = [], isLoading, isError } = useQuery({
    queryKey: ['aiq-connections'], queryFn: () => netappAPI.listConfigs(0, 100, false), refetchInterval: 5000,
  });
  const visible = configId ? configs.filter(c => c.id === configId) : configs;
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Breadcrumb items={[{ label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> }, { label: 'NetApp Configuration', href: '/settings/netapp-config', icon: <Database className="w-4 h-4" /> }]} />
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div><h1 className="text-2xl font-bold">NetApp AIQ connections</h1><p className="text-gray-500 mt-1">Connect once to discover and collect data for all your customers.</p></div>
          <button onClick={() => setEditing(null)} className="flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2"><Plus className="w-4 h-4" />Add connection</button>
        </div>
        {isLoading && <p role="status">Loading AIQ connections…</p>}
        {isError && <p role="alert">Unable to load AIQ connections. Please refresh the page.</p>}
        {!isLoading && !isError && visible.length === 0 && <p>No AIQ connection found. Add a connection using your NetApp refresh token.</p>}
        {visible.map(config => <ConnectionCard key={config.id} config={config} onEdit={() => setEditing(config)} />)}
      </div>
      {editing !== undefined && <ConfigurationWizard config={editing || undefined} onClose={() => setEditing(undefined)} onComplete={() => {
        setEditing(undefined);
        void queryClient.invalidateQueries({ queryKey: ['aiq-connections'] });
        void queryClient.invalidateQueries({ queryKey: ['aiq-customers'] });
        toast.success('AIQ connection saved. Customers will refresh in the background.');
      }} />}
    </DashboardLayout>
  );
}
